import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { IndentedTree } from '@ant-design/graphs';
import type { Graph, NodeData } from '@antv/g6';
import { transformDataForTree } from './skill-framework-chart-utils';
import type { SkillNode } from './skill-framework-chart-utils';

interface SkillFrameworkTreeProps {
    framework: SkillNode;
    height?: number | string;
}

const getNodeLabel = (node: NodeData): string => {
    if (typeof node.name === 'string') return node.name;

    const data = node.data;
    if (typeof data === 'object' && data !== null) {
        const name = (data as Record<string, unknown>).name;
        if (typeof name === 'string') return name;
    }

    return '';
};

const SkillFrameworkTree = ({ framework, height = 760 }: SkillFrameworkTreeProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const graphRef = useRef<Graph | null>(null);
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        if (typeof document === 'undefined') return false;
        return document.documentElement.classList.contains('dark');
    });

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const root = document.documentElement;
        const observer = new MutationObserver(() => {
            setIsDarkMode(root.classList.contains('dark'));
        });

        observer.observe(root, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            const graph = graphRef.current;
            if (graph) {
                graph.setSize(container.clientWidth, container.clientHeight);
                graph.fitView();
            }
        });

        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, []);

    const treeData = useMemo(() => transformDataForTree(framework), [framework]);

    return (
        <div
            ref={containerRef}
            className="bg-slate-50 dark:bg-slate-900 rounded-lg border p-3 w-full overflow-hidden"
            style={{ height: typeof height === 'number' ? `${height}px` : height }}
        >
            <IndentedTree
                data={treeData}
                labelField={getNodeLabel}
                defaultExpandLevel={1}
                type="boxed"
                direction="right"
                nodeMinWidth={260}
                nodeMaxWidth={260}
                zoomRange={[0.6, 1.6]}
                layout={{
                    type: 'indented',
                    direction: 'LR',
                    indent: 300,
                    getVGap: () => 30,
                }}
                node={{
                    style: {
                        lineWidth: 1,
                        stroke: isDarkMode ? '#64748b' : '#d1d5db',
                        fill: isDarkMode ? '#334155' : '#e2e8f0',
                        labelFill: isDarkMode ? '#f8fafc' : '#1f2937',
                        labelFontSize: 13,
                    },
                }}
                edge={{
                    style: {
                        stroke: isDarkMode ? '#94a3b8' : '#9ca3af',
                        lineWidth: 1.5,
                    },
                }}
                behaviors={[{ type: 'drag-canvas' }, { type: 'zoom-canvas', sensitivity: 0.15 }]}
                transforms={(prev) =>
                    prev.map((transform) =>
                        typeof transform === 'object' &&
                        transform !== null &&
                        'key' in transform &&
                        transform.key === 'collapse-expand-react-node'
                            ? {
                                  ...transform,
                                  enable: true,
                                  trigger: 'icon',
                                  iconType: 'plus-minus',
                                  refreshLayout: false,
                              }
                            : transform
                    )
                }
                onReady={(graph) => {
                    graphRef.current = graph;
                    graph.fitView();
                }}
            />
        </div>
    );
};

export default memo(SkillFrameworkTree);
